/**
 * quantumFieldPipeline — quantum harmonic oscillator visualization.
 *
 * Visualizes the probability density of a 2D quantum harmonic
 * oscillator. The wave function is rendered as a glowing
 * probability cloud. Multiple "states" (different n_x, n_y
 * quantum numbers) blend together, creating an ethereal
 * interference pattern.
 */
import { linkProgram, POST_ATTRIBS } from "./glContext";

export interface QuantumFieldPipeline {
  program: WebGLProgram;
  vao: WebGLVertexArrayObject | null;
  vbo: WebGLBuffer | null;
  uResolution: WebGLUniformLocation | null;
  uTime: WebGLUniformLocation | null;
  uBass: WebGLUniformLocation | null;
  uMid: WebGLUniformLocation | null;
  uHigh: WebGLUniformLocation | null;
  bands: Float32Array;
}

const VERT_SRC = `#version 300 es
precision highp float;
layout(location = 0) in vec2 aPos;
out vec2 vUV;
void main() {
  vUV = aPos * 0.5 + 0.5;
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

const FRAG_SRC = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 outColor;

uniform vec2 uResolution;
uniform float uTime;
uniform vec3 uBass;
uniform vec3 uMid;
uniform vec3 uHigh;

float hash21(vec2 p) {
  p = fract(p * vec2(123.34, 456.21));
  p += dot(p, p + 45.32);
  return fract(p.x * p.y);
}

// 2D quantum harmonic oscillator wave function: psi_{n,m}(x,y).
float psi(int n, int m, vec2 p) {
  // Hermite polynomials H_0, H_1, H_2, H_3, H_4 (simple form).
  float x = p.x;
  float y = p.y;
  // H_n(x) approximations for small n.
  float hx = 0.0;
  float hy = 0.0;
  if (n == 0) hx = 1.0;
  else if (n == 1) hx = 2.0 * x;
  else if (n == 2) hx = 4.0 * x * x - 2.0;
  else if (n == 3) hx = 8.0 * x * x * x - 12.0 * x;
  else hx = 16.0 * x * x * x * x - 48.0 * x * x + 12.0;
  if (m == 0) hy = 1.0;
  else if (m == 1) hy = 2.0 * y;
  else if (m == 2) hy = 4.0 * y * y - 2.0;
  else if (m == 3) hy = 8.0 * y * y * y - 12.0 * y;
  else hy = 16.0 * y * y * y * y - 48.0 * y * y + 12.0;
  // Gaussian envelope.
  float envelope = exp(-(x * x + y * y));
  return hx * hy * envelope;
}

// Probability density |psi|^2 of a sum of basis states.
float probability(vec2 p) {
  // Sum of 4 basis states with time-varying phase.
  float total = 0.0;
  // State (0, 0): ground state.
  float s00 = psi(0, 0, p);
  total += s00 * s00;
  // State (1, 0).
  float s10 = psi(1, 0, p) * sin(uTime * 0.5);
  total += s10 * s10;
  // State (0, 1).
  float s01 = psi(0, 1, p) * cos(uTime * 0.5 + 1.0);
  total += s01 * s01;
  // State (2, 1).
  float s21 = psi(2, 1, p) * sin(uTime * 0.7 + 2.0);
  total += s21 * s21;
  // State (1, 2).
  float s12 = psi(1, 2, p) * cos(uTime * 0.6 + 3.0);
  total += s12 * s12;
  return total;
}

void main() {
  float bass = (uBass.x + uBass.y + uBass.z) / 3.0;
  float mid = (uMid.x + uMid.y + uMid.z) / 3.0;
  float high = (uHigh.x + uHigh.y + uHigh.z) / 3.0;
  bass = pow(clamp(bass * 2.5, 0.0, 1.0), 0.7);
  mid = pow(clamp(mid * 3.0, 0.0, 1.0), 0.7);
  high = pow(clamp(high * 4.0, 0.0, 1.0), 0.7);

  // Aspect-corrected coords, scaled so the wave function fits.
  vec2 p = (gl_FragCoord.xy - uResolution * 0.5) / min(uResolution.x, uResolution.y);
  p *= 1.5;

  // Probability density.
  float prob = probability(p);

  // Color: cyan / violet / magenta gradient based on probability + phase.
  vec3 lowColor = vec3(0.10, 0.30, 0.55);
  vec3 midColor = vec3(0.85, 0.40, 0.95);
  vec3 highColor = vec3(0.95, 0.85, 0.55);
  vec3 col = mix(lowColor, midColor, smoothstep(0.0, 0.5, prob));
  col = mix(col, highColor, smoothstep(0.5, 1.5, prob));
  // Brightness boost.
  col *= 1.0 + prob * 2.0;
  // Audio: pulse with bass.
  col *= 1.0 + bass * 0.5;
  // Add an oscillating term for "quantum vibration" feel.
  col *= 1.0 + 0.1 * sin(uTime * 3.0 + length(p) * 5.0);

  // Dark background with subtle starfield.
  vec3 bg = vec3(0.01, 0.01, 0.03);
  float star = hash21(floor(gl_FragCoord.xy / 4.0));
  bg += vec3(0.85, 0.9, 1.0) * step(0.998, star) * 0.4;

  col = mix(bg, col, smoothstep(0.0, 0.05, prob));

  // Vignette.
  float vig = 1.0 - dot(p, p) * 0.15;
  col *= clamp(vig, 0.0, 1.0);

  // Tone mapping.
  col = (col * (2.51 * col + 0.03)) / (col * (2.43 * col + 0.59) + 0.14);
  col = pow(clamp(col, 0.0, 1.0), vec3(1.0 / 2.2));

  outColor = vec4(col, 1.0);
}
`;

export function computeQuantumFieldBands(t: number, out: Float32Array): void {
  out[0] = 0.5 + 0.3 * Math.sin(t * 0.5);
  out[1] = 0.5 + 0.3 * Math.sin(t * 0.7 + 1.0);
  out[2] = 0.4 + 0.3 * Math.sin(t * 1.0 + 2.0);
  out[3] = 0.5 + 0.3 * Math.sin(t * 1.2 + 1.5);
  out[4] = 0.5 + 0.3 * Math.sin(t * 1.4 + 0.5);
  out[5] = 0.4 + 0.3 * Math.sin(t * 1.7 + 2.5);
  out[6] = 0.3 + 0.4 * Math.sin(t * 2.1 + 0.7);
  out[7] = 0.4 + 0.3 * Math.sin(t * 2.5 + 1.8);
  out[8] = 0.3 + 0.4 * Math.sin(t * 3.0 + 3.0);
}

export function computeQuantumFieldRealtimeBands(
  freqData: Uint8Array,
  out: Float32Array,
): void {
  const N = freqData.length;
  const ranges = [
    [0.00, 0.030], [0.030, 0.080], [0.080, 0.150],
    [0.150, 0.260], [0.260, 0.380], [0.380, 0.500],
    [0.500, 0.660], [0.660, 0.820], [0.820, 0.980],
  ];
  for (let i = 0; i < 9; i++) {
    const [lo, hi] = ranges[i];
    const start = Math.floor(lo * N);
    const end = Math.floor(hi * N);
    let sum = 0, count = 0;
    for (let k = start; k < end; k++) {
      sum += freqData[k];
      count++;
    }
    out[i] = count > 0 ? (sum / count) / 255 : 0;
  }
}

export function createQuantumFieldPipeline(gl: WebGL2RenderingContext): QuantumFieldPipeline | null {
  const program = linkProgram(gl, VERT_SRC, FRAG_SRC, POST_ATTRIBS);
  if (!program) return null;
  const vao = gl.createVertexArray();
  const vbo = gl.createBuffer();
  if (!vao || !vbo) return null;
  const verts = new Float32Array([
    -1, -1,  1, -1,  -1, 1,
    -1,  1,  1, -1,   1, 1,
  ]);
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, verts, gl.STATIC_DRAW);
  gl.enableVertexAttribArray(POST_ATTRIBS.aPos);
  gl.vertexAttribPointer(POST_ATTRIBS.aPos, 2, gl.FLOAT, false, 0, 0);
  gl.bindVertexArray(null);
  return {
    program, vao, vbo,
    uResolution: gl.getUniformLocation(program, "uResolution"),
    uTime: gl.getUniformLocation(program, "uTime"),
    uBass: gl.getUniformLocation(program, "uBass"),
    uMid: gl.getUniformLocation(program, "uMid"),
    uHigh: gl.getUniformLocation(program, "uHigh"),
    bands: new Float32Array(9),
  };
}

export function drawQuantumField(
  gl: WebGL2RenderingContext,
  pipe: QuantumFieldPipeline,
  width: number,
  height: number,
  t: number,
): void {
  gl.useProgram(pipe.program);
  const prog = pipe.program;
  gl.uniform2f(gl.getUniformLocation(prog, "uResolution"), width, height);
  gl.uniform1f(gl.getUniformLocation(prog, "uTime"), t);
  gl.uniform3f(gl.getUniformLocation(prog, "uBass"), pipe.bands[0], pipe.bands[1], pipe.bands[2]);
  gl.uniform3f(gl.getUniformLocation(prog, "uMid"), pipe.bands[3], pipe.bands[4], pipe.bands[5]);
  gl.uniform3f(gl.getUniformLocation(prog, "uHigh"), pipe.bands[6], pipe.bands[7], pipe.bands[8]);
  gl.bindVertexArray(pipe.vao);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
  gl.bindVertexArray(null);
}
